import { s as scopedGlobals, l as logger, b as FileType } from "./chunks/logger.mjs";
import { F, a, c, N, P } from "./chunks/logger.mjs";
import { TypedEventTarget } from "typescript-event-target";
import isSvg from "is-svg";
import { getCapabilities } from "@nextcloud/capabilities";
import { extname, basename } from "@nextcloud/paths";
import { f, p } from "./chunks/fileSize.mjs";
import { getLanguage, getCanonicalLocale } from "@nextcloud/l10n";
class FilesRegistry extends TypedEventTarget {
}
function getRegistry() {
  scopedGlobals.registry ??= new FilesRegistry();
  return scopedGlobals.registry;
}
function getFilesRegistry() {
  return getRegistry();
}
const DefaultType = Object.freeze({
  DEFAULT: "default",
  HIDDEN: "hidden"
});
function registerFileAction(action) {
  validateAction$1(action);
  scopedGlobals.fileActions ??= /* @__PURE__ */ new Map();
  if (scopedGlobals.fileActions.has(action.id)) {
    logger.error(`FileAction ${action.id} already registered`, { action });
    return;
  }
  scopedGlobals.fileActions.set(action.id, action);
  getRegistry().dispatchTypedEvent("register:action", new CustomEvent("register:action", { detail: action }));
}
function getFileActions() {
  if (scopedGlobals.fileActions) {
    return [...scopedGlobals.fileActions.values()];
  }
  return [];
}
function validateAction$1(action) {
  if (!action.id || typeof action.id !== "string") {
    throw new Error("Invalid id");
  }
  if (!action.displayName || typeof action.displayName !== "function") {
    throw new Error("Invalid displayName function");
  }
  if ("title" in action && typeof action.title !== "function") {
    throw new Error("Invalid title function");
  }
  if (!action.iconSvgInline || typeof action.iconSvgInline !== "function") {
    throw new Error("Invalid iconSvgInline function");
  }
  if (!action.exec || typeof action.exec !== "function") {
    throw new Error("Invalid exec function");
  }
  if ("enabled" in action && typeof action.enabled !== "function") {
    throw new Error("Invalid enabled function");
  }
  if ("execBatch" in action && typeof action.execBatch !== "function") {
    throw new Error("Invalid execBatch function");
  }
  if ("order" in action && typeof action.order !== "number") {
    throw new Error("Invalid order");
  }
  if (action.destructive !== void 0 && typeof action.destructive !== "boolean") {
    throw new Error("Invalid destructive flag");
  }
  if ("parent" in action && typeof action.parent !== "string") {
    throw new Error("Invalid parent");
  }
  if (action.default && !Object.values(DefaultType).includes(action.default)) {
    throw new Error("Invalid default");
  }
  if ("inline" in action && typeof action.inline !== "function") {
    throw new Error("Invalid inline function");
  }
  if ("renderInline" in action && typeof action.renderInline !== "function") {
    throw new Error("Invalid renderInline function");
  }
  if ("hotkey" in action && action.hotkey !== void 0) {
    if (typeof action.hotkey !== "object") {
      throw new Error("Invalid hotkey configuration");
    }
    if (typeof action.hotkey.key !== "string" || !action.hotkey.key) {
      throw new Error("Missing or invalid hotkey key");
    }
    if (typeof action.hotkey.description !== "string" || !action.hotkey.description) {
      throw new Error("Missing or invalid hotkey description");
    }
  }
}
function registerFileListAction(action) {
  validateAction(action);
  scopedGlobals.fileListActions ??= /* @__PURE__ */ new Map();
  if (scopedGlobals.fileListActions.has(action.id)) {
    logger.error(`FileListAction with id "${action.id}" is already registered`, { action });
    return;
  }
  scopedGlobals.fileListActions.set(action.id, action);
  getRegistry().dispatchTypedEvent("register:listAction", new CustomEvent("register:listAction", { detail: action }));
}
function getFileListActions() {
  if (scopedGlobals.fileListActions) {
    return [...scopedGlobals.fileListActions.values()];
  }
  return [];
}
function validateAction(action) {
  if (!action.id || typeof action.id !== "string") {
    throw new Error("Invalid id");
  }
  if (!action.displayName || typeof action.displayName !== "function") {
    throw new Error("Invalid displayName function");
  }
  if ("iconSvgInline" in action && typeof action.iconSvgInline !== "function") {
    throw new Error("Invalid iconSvgInline function");
  }
  if ("order" in action && typeof action.order !== "number") {
    throw new Error("Invalid order");
  }
  if ("enabled" in action && typeof action.enabled !== "function") {
    throw new Error("Invalid enabled function");
  }
  if (!action.exec || typeof action.exec !== "function") {
    throw new Error("Invalid exec function");
  }
}
/*!
 * SPDX-FileCopyrightText: 2023 Nextcloud GmbH and Nextcloud contributors
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */
function registerFileListFilter(filter) {
  scopedGlobals.fileListFilters ??= /* @__PURE__ */ new Map();
  if (scopedGlobals.fileListFilters.has(filter.id)) {
    throw new Error(`File list filter "${filter.id}" already registered`);
  }
  scopedGlobals.fileListFilters.set(filter.id, filter);
  getRegistry().dispatchTypedEvent("register:listFilter", new CustomEvent("register:listFilter", { detail: filter }));
}
function unregisterFileListFilter(filterId) {
  if (scopedGlobals.fileListFilters && scopedGlobals.fileListFilters.has(filterId)) {
    scopedGlobals.fileListFilters.delete(filterId);
    getRegistry().dispatchTypedEvent("unregister:listFilter", new CustomEvent("unregister:listFilter", { detail: filterId }));
  }
}
function getFileListFilters() {
  if (scopedGlobals.fileListFilters) {
    return [...scopedGlobals.fileListFilters.values()];
  }
  return [];
}
/*!
 * SPDX-FileCopyrightText: 2023 Nextcloud GmbH and Nextcloud contributors
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */
class FileListFilter extends TypedEventTarget {
  id;
  order;
  constructor(id, order = 100) {
    super();
    this.id = id;
    this.order = order;
  }
  filter(nodes) {
    throw new Error("Not implemented");
  }
  updateChips(chips) {
    this.dispatchTypedEvent("update:chips", new CustomEvent("update:chips", { detail: chips }));
  }
  filterUpdated() {
    this.dispatchTypedEvent("update:filter", new CustomEvent("update:filter"));
  }
}
function registerFileListHeader(header) {
  validateHeader(header);
  scopedGlobals.fileListHeaders ??= /* @__PURE__ */ new Map();
  if (scopedGlobals.fileListHeaders.has(header.id)) {
    logger.error(`Header ${header.id} already registered`, { header });
    return;
  }
  scopedGlobals.fileListHeaders.set(header.id, header);
  getRegistry().dispatchTypedEvent("register:listHeader", new CustomEvent("register:listHeader", { detail: header }));
}
function getFileListHeaders() {
  if (!scopedGlobals.fileListHeaders) {
    return [];
  }
  return [...scopedGlobals.fileListHeaders.values()];
}
function validateHeader(header) {
  if (!header.id || !header.render || !header.updated) {
    throw new Error("Invalid header: id, render and updated are required");
  }
  if (typeof header.id !== "string") {
    throw new Error("Invalid id property");
  }
  if (header.enabled !== void 0 && typeof header.enabled !== "function") {
    throw new Error("Invalid enabled property");
  }
  if (header.render && typeof header.render !== "function") {
    throw new Error("Invalid render property");
  }
  if (header.updated && typeof header.updated !== "function") {
    throw new Error("Invalid updated property");
  }
}
function checkOptionalProperty(obj, property, type) {
  if (typeof obj[property] !== "undefined") {
    if (type === "array") {
      if (!Array.isArray(obj[property])) {
        throw new Error(`View ${property} must be an array`);
      }
    } else if (typeof obj[property] !== type) {
      throw new Error(`View ${property} must be a ${type}`);
    } else if (type === "object" && (obj[property] === null || Array.isArray(obj[property]))) {
      throw new Error(`View ${property} must be an object`);
    }
  }
}
class Column {
  _column;
  constructor(column) {
    validateColumn(column);
    this._column = column;
  }
  get id() {
    return this._column.id;
  }
  get title() {
    return this._column.title;
  }
  get render() {
    return this._column.render;
  }
  get sort() {
    return this._column.sort;
  }
  get summary() {
    return this._column.summary;
  }
}
function validateColumn(column) {
  if (typeof column !== "object" || column === null) {
    throw new Error("View column must be an object");
  }
  if (!column.id || typeof column.id !== "string") {
    throw new Error("A column id is required");
  }
  if (!column.title || typeof column.title !== "string") {
    throw new Error("A column title is required");
  }
  if (!column.render || typeof column.render !== "function") {
    throw new Error("A render function is required");
  }
  checkOptionalProperty(column, "sort", "function");
  checkOptionalProperty(column, "summary", "function");
}
class View {
  _view;
  constructor(view) {
    validateView(view);
    this._view = view;
  }
  get id() {
    return this._view.id;
  }
  get name() {
    return this._view.name;
  }
  get caption() {
    return this._view.caption;
  }
  get emptyTitle() {
    return this._view.emptyTitle;
  }
  get emptyCaption() {
    return this._view.emptyCaption;
  }
  get getContents() {
    return this._view.getContents;
  }
  get hidden() {
    return this._view.hidden;
  }
  get icon() {
    return this._view.icon;
  }
  set icon(icon) {
    this._view.icon = icon;
  }
  get order() {
    return this._view.order;
  }
  set order(order) {
    this._view.order = order;
  }
  get params() {
    return this._view.params;
  }
  set params(params) {
    this._view.params = params;
  }
  get columns() {
    return this._view.columns;
  }
  get emptyView() {
    return this._view.emptyView;
  }
  get parent() {
    return this._view.parent;
  }
  get sticky() {
    return this._view.sticky;
  }
  get expanded() {
    return this._view.expanded;
  }
  set expanded(expanded) {
    this._view.expanded = expanded;
  }
  get defaultSortKey() {
    return this._view.defaultSortKey;
  }
  get loadChildViews() {
    return this._view.loadChildViews;
  }
}
function validateView(view) {
  if (!view.icon || typeof view.icon !== "string" || !isSvg(view.icon)) {
    throw new Error("View icon is required and must be a valid svg string");
  }
  if (!view.id || typeof view.id !== "string") {
    throw new Error("View id is required and must be a string");
  }
  if (!view.getContents || typeof view.getContents !== "function") {
    throw new Error("View getContents is required and must be a function");
  }
  if (!view.name || typeof view.name !== "string") {
    throw new Error("View name is required and must be a string");
  }
  checkOptionalProperty(view, "caption", "string");
  checkOptionalProperty(view, "columns", "array");
  checkOptionalProperty(view, "defaultSortKey", "string");
  checkOptionalProperty(view, "emptyCaption", "string");
  checkOptionalProperty(view, "emptyTitle", "string");
  checkOptionalProperty(view, "emptyView", "function");
  checkOptionalProperty(view, "expanded", "boolean");
  checkOptionalProperty(view, "hidden", "boolean");
  checkOptionalProperty(view, "loadChildViews", "function");
  checkOptionalProperty(view, "order", "number");
  checkOptionalProperty(view, "params", "object");
  checkOptionalProperty(view, "parent", "string");
  checkOptionalProperty(view, "sticky", "boolean");
  if (view.columns) {
    view.columns.forEach(validateColumn);
    const columnIds = view.columns.reduce((set, column) => set.add(column.id), /* @__PURE__ */ new Set());
    if (columnIds.size !== view.columns.length) {
      throw new Error("View columns must have unique ids");
    }
  }
}
class Navigation extends TypedEventTarget {
  _views = [];
  _currentView = null;
  /**
   * Register a new view on the navigation
   *
   * @param views The views to register
   * @throws {Error} if a view with the same id is already registered
   * @throws {Error} if the registered view is invalid
   */
  register(...views) {
    for (const view of views) {
      if (this._views.find((search) => search.id === view.id)) {
        throw new Error(`IView id ${view.id} is already registered`);
      }
      validateView(view);
    }
    this._views.push(...views);
    this.dispatchTypedEvent("update", new CustomEvent("update"));
  }
  /**
   * Remove a registered view
   *
   * @param id The id of the view to remove
   */
  remove(id) {
    const index = this._views.findIndex((view) => view.id === id);
    if (index !== -1) {
      this._views.splice(index, 1);
      this.dispatchTypedEvent("update", new CustomEvent("update"));
    }
  }
  /**
   * Set the currently active view
   *
   * @param id - The id of the view to set as active
   * @throws {Error} If no view with the given id was registered
   * @fires UpdateActiveViewEvent
   */
  setActive(id) {
    if (id === null) {
      this._currentView = null;
    } else {
      const view = this._views.find(({ id: viewId }) => viewId === id);
      if (!view) {
        throw new Error(`No view with ${id} registered`);
      }
      this._currentView = view;
    }
    const event = new CustomEvent("updateActive", { detail: this._currentView });
    this.dispatchTypedEvent("updateActive", event);
  }
  /**
   * The currently active files view
   */
  get active() {
    return this._currentView;
  }
  /**
   * All registered views
   */
  get views() {
    return this._views;
  }
}
function getNavigation() {
  scopedGlobals.navigation ??= new Navigation();
  return scopedGlobals.navigation;
}
const NewMenuEntryCategory = Object.freeze({
  /**
   * For actions where the user is intended to upload from their device
   */
  UploadFromDevice: 0,
  /**
   * For actions that create new nodes on the server without uploading
   */
  CreateNew: 1,
  /**
   * For everything not matching the other categories
   */
  Other: 2
});
class NewMenu {
  _entries = [];
  registerEntry(entry) {
    this.validateEntry(entry);
    entry.category = entry.category ?? NewMenuEntryCategory.CreateNew;
    this._entries.push(entry);
  }
  unregisterEntry(entry) {
    const entryIndex = typeof entry === "string" ? this.getEntryIndex(entry) : this.getEntryIndex(entry.id);
    if (entryIndex === -1) {
      logger.warn("Entry not found, nothing removed", { entry, entries: this.getEntries() });
      return;
    }
    this._entries.splice(entryIndex, 1);
  }
  /**
   * Get the list of registered entries
   *
   * @param context - The creation context. Usually the current folder
   */
  getEntries(context) {
    if (context) {
      return this._entries.filter((entry) => typeof entry.enabled === "function" ? entry.enabled(context) : true);
    }
    return this._entries;
  }
  getEntryIndex(id) {
    return this._entries.findIndex((entry) => entry.id === id);
  }
  validateEntry(entry) {
    if (!entry.id || !entry.displayName || !entry.iconSvgInline || !entry.handler) {
      throw new Error("Invalid entry");
    }
    if (typeof entry.id !== "string" || typeof entry.displayName !== "string") {
      throw new Error("Invalid id or displayName property");
    }
    if (entry.iconSvgInline && typeof entry.iconSvgInline !== "string") {
      throw new Error("Invalid icon provided");
    }
    if (entry.enabled !== void 0 && typeof entry.enabled !== "function") {
      throw new Error("Invalid enabled property");
    }
    if (typeof entry.handler !== "function") {
      throw new Error("Invalid handler property");
    }
    if ("order" in entry && typeof entry.order !== "number") {
      throw new Error("Invalid order property");
    }
    if (this.getEntryIndex(entry.id) !== -1) {
      throw new Error("Duplicate entry");
    }
  }
}
function getNewFileMenu() {
  scopedGlobals.newFileMenu ??= new NewMenu();
  return scopedGlobals.newFileMenu;
}
function addNewFileMenuEntry(entry) {
  const newFileMenu = getNewFileMenu();
  return newFileMenu.registerEntry(entry);
}
function removeNewFileMenuEntry(entry) {
  const newFileMenu = getNewFileMenu();
  return newFileMenu.unregisterEntry(entry);
}
function getNewFileMenuEntries(context) {
  const newFileMenu = getNewFileMenu();
  return newFileMenu.getEntries(context).sort((a2, b) => {
    if (a2.order !== void 0 && b.order !== void 0 && a2.order !== b.order) {
      return a2.order - b.order;
    }
    return a2.displayName.localeCompare(b.displayName, void 0, { numeric: true, sensitivity: "base" });
  });
}
function registerSidebarAction(action) {
  validateSidebarAction(action);
  scopedGlobals.filesSidebarActions ??= /* @__PURE__ */ new Map();
  if (scopedGlobals.filesSidebarActions.has(action.id)) {
    logger.warn(`Sidebar action with id "${action.id}" already registered. Skipping.`);
    return;
  }
  scopedGlobals.filesSidebarActions.set(action.id, action);
  logger.debug(`New sidebar action with id "${action.id}" registered.`);
}
function getSidebarActions() {
  if (scopedGlobals.filesSidebarActions) {
    return [...scopedGlobals.filesSidebarActions.values()];
  }
  return [];
}
function validateSidebarAction(action) {
  if (typeof action !== "object") {
    throw new Error("Sidebar action is not an object");
  }
  if (!action.id || typeof action.id !== "string" || action.id !== CSS.escape(action.id)) {
    throw new Error("Sidebar actions need to have an id conforming to the HTML id attribute specifications");
  }
  if (!action.displayName || typeof action.displayName !== "function") {
    throw new Error("Sidebar actions need to have a displayName function");
  }
  if (!action.iconSvgInline || typeof action.iconSvgInline !== "function") {
    throw new Error("Sidebar actions need to have a iconSvgInline function");
  }
  if (!action.enabled || typeof action.enabled !== "function") {
    throw new Error("Sidebar actions need to have an enabled function");
  }
  if (!action.onClick || typeof action.onClick !== "function") {
    throw new Error("Sidebar actions need to have an onClick function");
  }
}
function registerSidebarTab(tab) {
  validateSidebarTab(tab);
  scopedGlobals.filesSidebarTabs ??= /* @__PURE__ */ new Map();
  if (scopedGlobals.filesSidebarTabs.has(tab.id)) {
    logger.warn(`Sidebar tab with id "${tab.id}" already registered. Skipping.`);
    return;
  }
  scopedGlobals.filesSidebarTabs.set(tab.id, tab);
  logger.debug(`New sidebar tab with id "${tab.id}" registered.`);
}
function getSidebarTabs() {
  if (scopedGlobals.filesSidebarTabs) {
    return [...scopedGlobals.filesSidebarTabs.values()];
  }
  return [];
}
function validateSidebarTab(tab) {
  if (typeof tab !== "object") {
    throw new Error("Sidebar tab is not an object");
  }
  if (!tab.id || typeof tab.id !== "string" || tab.id !== CSS.escape(tab.id)) {
    throw new Error("Sidebar tabs need to have an id conforming to the HTML id attribute specifications");
  }
  if (!tab.tagName || typeof tab.tagName !== "string") {
    throw new Error("Sidebar tabs need to have the tagName name set");
  }
  if (!tab.tagName.match(/^[a-z][a-z0-9-_]+$/)) {
    throw new Error('Sidebar tab "tagName" is invalid');
  }
  if (!tab.displayName || typeof tab.displayName !== "string") {
    throw new Error("Sidebar tabs need to have a name set");
  }
  if (typeof tab.iconSvgInline !== "string" || !isSvg(tab.iconSvgInline)) {
    throw new Error("Sidebar tabs need to have an valid SVG icon");
  }
  if (typeof tab.order !== "number") {
    throw new Error("Sidebar tabs need to have a numeric order set");
  }
  if (tab.enabled && typeof tab.enabled !== "function") {
    throw new Error('Sidebar tab "enabled" is not a function');
  }
  if (tab.onInit && typeof tab.onInit !== "function") {
    throw new Error('Sidebar tab "onInit" is not a function');
  }
}
class SidebarProxy {
  get #impl() {
    return window.OCA?.Files?._sidebar?.();
  }
  get available() {
    return !!this.#impl;
  }
  get isOpen() {
    return this.#impl?.isOpen ?? false;
  }
  get activeTab() {
    return this.#impl?.activeTab;
  }
  get node() {
    return this.#impl?.node;
  }
  open(node, tab) {
    this.#impl?.open(node, tab);
  }
  close() {
    this.#impl?.close();
  }
  setActiveTab(tabId) {
    this.#impl?.setActiveTab(tabId);
  }
  setFullScreenMode(isFullScreen) {
    this.#impl?.setFullScreenMode(isFullScreen);
  }
  mount(target) {
    const mountSidebar = window.OCA?.Files?._mountSidebar;
    if (mountSidebar === void 0) {
      logger.error("Cannot render the sidebar as it was not loaded for this page, see the `LoadSidebar` event.");
      return;
    }
    mountSidebar(target);
  }
  registerTab(tab) {
    registerSidebarTab(tab);
  }
  getTabs(context) {
    return this.#impl?.getTabs(context) ?? [];
  }
  getActions(context) {
    return this.#impl?.getActions(context) ?? [];
  }
  registerAction(action) {
    registerSidebarAction(action);
  }
}
function getSidebar() {
  return new SidebarProxy();
}
const InvalidFilenameErrorReason = Object.freeze({
  ReservedName: "reserved name",
  Character: "character",
  Extension: "extension"
});
class InvalidFilenameError extends Error {
  constructor(options) {
    super(`Invalid ${options.reason} '${options.segment}' in filename '${options.filename}'`, { cause: options });
  }
  /**
   * The filename that was validated
   */
  get filename() {
    return this.cause.filename;
  }
  /**
   * Reason why the validation failed
   */
  get reason() {
    return this.cause.reason;
  }
  /**
   * Part of the filename that caused this error
   */
  get segment() {
    return this.cause.segment;
  }
}
function validateFilename(filename) {
  const capabilities = getCapabilities().files;
  const forbiddenCharacters = capabilities.forbidden_filename_characters ?? ["/", "\\"];
  for (const character of forbiddenCharacters) {
    if (filename.includes(character)) {
      throw new InvalidFilenameError({ segment: character, reason: InvalidFilenameErrorReason.Character, filename });
    }
  }
  filename = filename.toLocaleLowerCase();
  const forbiddenFilenames = capabilities.forbidden_filenames ?? [".htaccess"];
  if (forbiddenFilenames.includes(filename)) {
    throw new InvalidFilenameError({ filename, segment: filename, reason: InvalidFilenameErrorReason.ReservedName });
  }
  const endOfBasename = filename.indexOf(".", 1);
  const basename2 = filename.substring(0, endOfBasename === -1 ? void 0 : endOfBasename);
  const forbiddenFilenameBasenames = capabilities.forbidden_filename_basenames ?? [];
  if (forbiddenFilenameBasenames.includes(basename2)) {
    throw new InvalidFilenameError({ filename, segment: basename2, reason: InvalidFilenameErrorReason.ReservedName });
  }
  const forbiddenFilenameExtensions = capabilities.forbidden_filename_extensions ?? [];
  for (const extension of forbiddenFilenameExtensions) {
    if (filename.length > extension.length && filename.endsWith(extension)) {
      throw new InvalidFilenameError({ segment: extension, reason: InvalidFilenameErrorReason.Extension, filename });
    }
  }
}
function isFilenameValid(filename) {
  try {
    validateFilename(filename);
    return true;
  } catch (error) {
    if (error instanceof InvalidFilenameError) {
      return false;
    }
    throw error;
  }
}
function getUniqueName(name, otherNames, options) {
  const opts = {
    suffix: (n) => `(${n})`,
    ignoreFileExtension: false,
    ...options
  };
  let newName = name;
  let i = 1;
  while (otherNames.includes(newName)) {
    const ext = opts.ignoreFileExtension ? "" : extname(name);
    const base = basename(name, ext);
    newName = `${base} ${opts.suffix(i++)}${ext}`;
  }
  return newName;
}
function stringify(value) {
  if (value instanceof Date) {
    return value.toISOString();
  }
  return String(value);
}
function orderBy(collection, identifiers, orders) {
  identifiers = identifiers ?? [(value) => value];
  orders = orders ?? [];
  const sorting = identifiers.map((_, index) => (orders[index] ?? "asc") === "asc" ? 1 : -1);
  const collator = Intl.Collator(
    [getLanguage(), getCanonicalLocale()],
    {
      // handle 10 as ten and not as one-zero
      numeric: true,
      usage: "sort"
    }
  );
  return [...collection].sort((a2, b) => {
    for (const [index, identifier] of identifiers.entries()) {
      const value = collator.compare(stringify(identifier(a2)), stringify(identifier(b)));
      if (value !== 0) {
        return value * sorting[index];
      }
    }
    return 0;
  });
}
const FilesSortingMode = Object.freeze({
  Name: "basename",
  Modified: "mtime",
  Size: "size"
});
function sortNodes(nodes, options = {}) {
  const sortingOptions = {
    // Default to sort by name
    sortingMode: FilesSortingMode.Name,
    // Default to sort ascending
    sortingOrder: "asc",
    ...options
  };
  function basename2(node) {
    const name = node.displayname || node.attributes?.displayname || node.basename || "";
    if (node.type === FileType.Folder) {
      return name;
    }
    return name.lastIndexOf(".") > 0 ? name.slice(0, name.lastIndexOf(".")) : name;
  }
  const identifiers = [
    // 1: Sort favorites first if enabled
    ...sortingOptions.sortFavoritesFirst ? [(v) => v.attributes?.favorite !== 1] : [],
    // 2: Sort folders first if sorting by name
    ...sortingOptions.sortFoldersFirst ? [(v) => v.type !== "folder"] : [],
    // 3: Use sorting mode if NOT basename (to be able to use display name too)
    ...sortingOptions.sortingMode !== FilesSortingMode.Name ? [(v) => v[sortingOptions.sortingMode] ?? v.attributes[sortingOptions.sortingMode]] : [],
    // 4: Use display name if available, fallback to name
    (v) => basename2(v),
    // 5: Finally, use basename if all previous sorting methods failed
    (v) => v.basename
  ];
  const orders = [
    // (for 1): always sort favorites before normal files
    ...sortingOptions.sortFavoritesFirst ? ["asc"] : [],
    // (for 2): always sort folders before files
    ...sortingOptions.sortFoldersFirst ? ["asc"] : [],
    // (for 3): Reverse if sorting by mtime as mtime higher means edited more recent -> lower
    ...sortingOptions.sortingMode === FilesSortingMode.Modified ? [sortingOptions.sortingOrder === "asc" ? "desc" : "asc"] : [],
    // (also for 3 so make sure not to conflict with 2 and 3)
    ...sortingOptions.sortingMode !== FilesSortingMode.Modified && sortingOptions.sortingMode !== FilesSortingMode.Name ? [sortingOptions.sortingOrder] : [],
    // for 4: use configured sorting direction
    sortingOptions.sortingOrder,
    // for 5: use configured sorting direction
    sortingOptions.sortingOrder
  ];
  return orderBy(nodes, identifiers, orders);
}
export {
  Column,
  DefaultType,
  F as File,
  FileListFilter,
  FileType,
  FilesSortingMode,
  a as Folder,
  InvalidFilenameError,
  InvalidFilenameErrorReason,
  Navigation,
  NewMenuEntryCategory,
  c as Node,
  N as NodeStatus,
  P as Permission,
  View,
  addNewFileMenuEntry,
  f as formatFileSize,
  getFileActions,
  getFileListActions,
  getFileListFilters,
  getFileListHeaders,
  getFilesRegistry,
  getNavigation,
  getNewFileMenu,
  getNewFileMenuEntries,
  getSidebar,
  getSidebarActions,
  getSidebarTabs,
  getUniqueName,
  isFilenameValid,
  orderBy,
  p as parseFileSize,
  registerFileAction,
  registerFileListAction,
  registerFileListFilter,
  registerFileListHeader,
  registerSidebarAction,
  registerSidebarTab,
  removeNewFileMenuEntry,
  sortNodes,
  unregisterFileListFilter,
  validateColumn,
  validateFilename,
  validateView
};
//# sourceMappingURL=index.mjs.map
